import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { PassIndex, RootState, TrackedSessionDashboardMode } from '../../../../../types/common.types';
import { PassDetail, TrackedSessionDashboard } from '../../../../../types/entity.types';

import { useTrackedSessionDashboardApi } from '../../../../../api/tracked-session-dashboard/useTrackedSessionDashboardApi';

import { flattenPassIndices, groupPassDetailByTeam } from '../../../../../utils/accountability.util';
import { fireConfetti } from '../../../../../utils/confetti.util';

import AccountabilityTrackedSessionDashboard from '../../../../../components/screens/programme/accountability/accountability-sessions/accountability-tracked-session-dashboard/AccountabilityTrackedSessionDashboard';

const PASS_DETAIL_POLL_INTERVAL_MS = 1000;
const AUTO_ADVANCE_INTERVAL_MS = 5000;
const INITIAL_PASS_INDEX: PassIndex = { index: -1 };

type Props = {
  sessionEventExternalReference?: string;
  onClose: () => void;
};

const AccountabilityTrackedSessionDashboardContainer = ({ sessionEventExternalReference, onClose }: Props) => {
  const programme = useSelector((state: RootState) => state.programme.programme);

  const [dashboard, setDashboard] = useState<TrackedSessionDashboard>();
  const [passDetail, setPassDetail] = useState<PassDetail>();
  const [activePassIndex, setActivePassIndex] = useState<PassIndex>(INITIAL_PASS_INDEX);
  const [mode, setMode] = useState<TrackedSessionDashboardMode>('Individual');
  const [isPaused, setIsPaused] = useState(true);

  const isFetchingPassDetailRef = useRef(false);
  const previousEntryCountRef = useRef<number | null>(null);

  const passIndices = useMemo(() => (dashboard ? flattenPassIndices(dashboard) : []), [dashboard]);

  const { retrieveDashboardStructure, retrieveDashboardDetail } = useTrackedSessionDashboardApi();

  useEffect(() => {
    if (isPaused || passIndices.length === 0) return;

    const intervalId = setInterval(() => {
      setActivePassIndex((currentPassIndex) => {
        const currentIndex = passIndices.findIndex(
          (passIndex) => passIndex.index === currentPassIndex.index && passIndex.subIndex === currentPassIndex.subIndex
        );

        return passIndices[(currentIndex + 1) % passIndices.length]!;
      });
    }, AUTO_ADVANCE_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isPaused, passIndices]);

  useEffect(() => {
    setDashboard(undefined);
    setPassDetail(undefined);
    setActivePassIndex(INITIAL_PASS_INDEX);
    setIsPaused(true);

    if (!sessionEventExternalReference) return;

    retrieveDashboardStructure(
      {
        sessionEventExternalReference
      },
      (data) => {
        setDashboard(data.trackedSessionDashboard);
      }
    );
  }, [sessionEventExternalReference, retrieveDashboardStructure]);

  useEffect(() => {
    if (!sessionEventExternalReference) return;

    previousEntryCountRef.current = null;
    setPassDetail(undefined);

    const fetchPassDetail = () => {
      if (isFetchingPassDetailRef.current) return;

      isFetchingPassDetailRef.current = true;

      retrieveDashboardDetail(
        {
          sessionEventExternalReference,
          checkpointIndex: activePassIndex.index,
          checkpointLapIndex: activePassIndex.subIndex
        },
        (data) => {
          isFetchingPassDetailRef.current = false;
          setPassDetail(data.passDetail);
        },
        () => {
          isFetchingPassDetailRef.current = false;
        }
      );
    };

    fetchPassDetail();
    const intervalId = setInterval(fetchPassDetail, PASS_DETAIL_POLL_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      isFetchingPassDetailRef.current = false;
    };
  }, [sessionEventExternalReference, activePassIndex, retrieveDashboardDetail]);

  const groupedPassDetail = useMemo(() => {
    if (!passDetail) return undefined;

    if (mode === 'Individual') return passDetail;

    return groupPassDetailByTeam(passDetail);
  }, [passDetail, mode]);

  useEffect(() => {
    previousEntryCountRef.current = null;
  }, [mode]);

  useEffect(() => {
    if (!groupedPassDetail) return;

    const entryCount = groupedPassDetail.leaderboard.length;

    if (previousEntryCountRef.current !== null && entryCount > previousEntryCountRef.current) {
      fireConfetti();
    }

    previousEntryCountRef.current = entryCount;
  }, [groupedPassDetail]);

  const handleCloseClick = () => {
    setMode('Individual');
    setDashboard(undefined);
    setPassDetail(undefined);
    setActivePassIndex(INITIAL_PASS_INDEX);
    setIsPaused(true);
    onClose();
  };

  return (
    <AccountabilityTrackedSessionDashboard
      show={!!sessionEventExternalReference}
      programme={programme}
      activePassIndex={activePassIndex}
      passIndices={passIndices}
      mode={mode}
      isPaused={isPaused}
      dashboard={dashboard}
      passDetail={groupedPassDetail}
      onClose={handleCloseClick}
      onPassOverviewClick={setActivePassIndex}
      onTogglePause={() => setIsPaused((prev) => !prev)}
      onModeClick={setMode}
    />
  );
};

export default AccountabilityTrackedSessionDashboardContainer;
